import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet, Text, Pressable, Platform } from 'react-native';
import Head from 'expo-router/head';
import { router } from 'expo-router';
import { CreatePostCoachmark, SaveAccountToast } from '../../src/components/onboarding';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import BottomSheet, { BottomSheetView } from '@gorhom/bottom-sheet';
import Map, { MapHandle } from '@components/map';
import { DEFAULT_REGION } from '@components/map/types';
import { AddressSearchBar, AddressSearchBarHandle } from '@components/map/AddressSearchBar';
import { StructureDetailSheet, StructureDetailPanel } from '@components/map/StructureDetailSheet';
import { MapSignInOverlay } from '../../src/components/map/MapSignInOverlay';
import { MapPathsOverlay } from '../../src/components/map/MapPathsOverlay';
import { MapLoadingOverlay } from '../../src/components/map/MapLoadingOverlay';
import { MapLegend } from '../../src/components/map/MapLegend';
import { DefensePlanPanel } from '../../src/components/map/DefensePlanPanel.web';
import { DefensePlanViewer } from '../../src/components/map/DefensePlanViewer.web';
import { DefensePlanViewerCard } from '../../src/components/map/DefensePlanViewerCard.web';
import { MapLayersPanel } from '../../src/components/map/MapLayersPanel.web';
import { PeoplePanel } from '../../src/components/map/PeoplePanel.web';
import { PROGRAM_PANEL_HEADING_ID } from '../../src/constants/program';
import { useTheme, SPACING, TYPOGRAPHY } from '../../src/theme';
// PLAN_PANEL_WIDTH isn't re-exported from the theme barrel (../../src/theme
// only lists a subset of theme/layout.ts) — uiStore.ts imports it the same
// direct way.
import { PLAN_PANEL_WIDTH, DETAIL_PANEL_WIDTH } from '../../src/theme/layout';
import { PostDetailPanel, POST_PANEL_HEADING_ID } from '../../src/components/posts/PostDetailPanel';
import { PropertyDockPanel } from '../../src/components/structures/PropertyDockPanel.web';
import { ComposerDockPanel } from '../../src/components/map/ComposerDockPanel.web';
import { Z_INDEX_AUTO, TRANSITION_DOCK_INSET } from '../../src/utils/webStyles';
import { useResponsiveLayout } from '@hooks/useResponsiveLayout';
import { useDesktopShell } from '@hooks/useDesktopShell';
import { useFieldMode } from '@hooks/useFieldMode';
import { useDesktopSheetStyle } from '../../src/hooks/useDesktopSheetStyle';
import { useMapSubscriptions } from '../../src/hooks/useMapSubscriptions';
import { useMapFocus } from '../../src/hooks/useMapFocus';
import { usePanelHistoryEntry } from '../../src/hooks/usePanelHistory';
import { usePanelDeepLinks } from '../../src/hooks/usePanelDeepLinks';
import { usePropertyLinkIntake } from '../../src/hooks/usePropertyLinkIntake';
import { usePlanSurfaceOpen } from '../../src/hooks/usePlanSurfaceOpen';
import { DockedPanel } from '../../src/components/layout/DockedPanel';
import { structureFocusInsets } from '../../src/components/map/clearViewCenter';
import { useLeavingPanel } from '../../src/hooks/useLeavingPanel';
import { ErrorBoundary } from '../../src/components/common/ErrorBoundary';
import { MapRegion, Post, ServiceArea, Structure } from '../../src/types';
import { useStructuresStore } from '../../src/store/structuresStore';
import { usePostsStore } from '../../src/store/postsStore';
import { useAuthStore } from '../../src/store/authStore';
import { useServiceAreasStore } from '../../src/store/serviceAreasStore';
import { useDefensePlansStore } from '../../src/store/defensePlansStore';
import { useUIStore, useLeftDockInset } from '../../src/store/uiStore';

// Moderator-only placement editor — the row below already gates its mount
// on placingOverlayId, so code-splitting it just means the chunk loads on
// that same gate instead of shipping in the entry bundle for everyone. The
// Suspense boundary around it is wrapped in an ErrorBoundary: a failed
// chunk load (a moderator on a tab left open across a deploy, fetching a
// bundle hash that's gone) would otherwise throw past the Suspense
// fallback and leave a silent blank panel with no way back in short of a
// manual refresh.
const LazyPlanMapPlacementPanel = React.lazy(() =>
  import('../../src/components/map/PlanMapPlacementPanel.web').then((m) => ({
    default: m.PlanMapPlacementPanel,
  }))
);

// The Program panel's body (moderators only) is lazy for the same reason as
// the placement editor above: everyone else never opens it.
const LazyProgramPanelBody = React.lazy(() =>
  import('../../src/components/map/ProgramPanel.web').then((m) => ({
    default: m.ProgramPanelBody,
  }))
);

export default function MapScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { isMobile } = useResponsiveLayout();
  const isDesktopShell = useDesktopShell();
  const fieldMode = useFieldMode();
  const sheetStyle = useDesktopSheetStyle();
  const planPanelOpen = useUIStore((s) => s.planPanelOpen);
  const layersPanelOpen = useUIStore((s) => s.layersPanelOpen);
  const peoplePanelOpen = useUIStore((s) => s.peoplePanelOpen);
  const programPanelOpen = useUIStore((s) => s.programPanelOpen);
  const closePlanPanel = useUIStore((s) => s.closePlanPanel);
  const setViewedPlanId = useUIStore((s) => s.setViewedPlanId);
  // Right-docked observation panel (panel-system.md Phase 2) — same right
  // surface as a selected structure and the presentation, so opening one
  // clears the others (see handlePostPress/handleStructurePress below and
  // Map.web.tsx's handleOpenSlideshow).
  const dockedPostId = useUIStore((s) => s.dockedPostId);
  const openDockedPost = useUIStore((s) => s.openDockedPost);
  const closeDockedPost = useUIStore((s) => s.closeDockedPost);
  // Browser history for the post panel (panel-system.md's "Deep links and
  // back", Phase 4A) — desktop shell only. Any other closeDockedPost() call
  // site (opening a structure, the presentation, and so on) still pops the
  // entry via usePanelHistoryEntry's own `active` watch; this wrapped closer
  // only matters for the panel's own X button.
  const closeDockedPostAndPop = usePanelHistoryEntry(
    isDesktopShell,
    dockedPostId != null,
    'post-panel',
    closeDockedPost
  );
  const requestSlideshowClose = useUIStore((s) => s.requestSlideshowClose);
  // Same right-hand surface again (panel-system.md Phase 3A) — the docked
  // composer (ComposerDockPanel.web.tsx).
  const closeComposerDock = useUIStore((s) => s.closeComposerDock);
  // Closes the addProperty/propertyPicker dock (PropertyDockPanel.web.tsx)
  // when one of the other right-docked surfaces opens — the mirror of that
  // panel's own open effect, which clears these three.
  const closeSheet = useUIStore((s) => s.closeSheet);
  // The plan the read-only viewer shows: someone else's opened plan, or the
  // owner's own plan reached the same way (a "plans in this area" card, a
  // share link). Hidden while the editor is docked — editing and viewing are
  // the same right-hand surface, and the editor takes it (see the dock
  // arbitration comments on handleStructurePress/handleOpenSlideshow below).
  const viewedPlan = useDefensePlansStore((s) => s.viewedPlan);
  // Set when viewedPlan just went null because the plan it pointed at
  // stopped being available (deleted, or made private) — see
  // defensePlansStore.subscribeViewedPlan's permission-denied branch. The
  // viewer stays mounted long enough to show this before closing itself.
  const viewedPlanNotice = useDefensePlansStore((s) => s.viewedPlanNotice);
  // Same condition as usePlanSurfaceOpen's viewerOpen — lifted there so
  // AppShell's sidebar-hide check can't drift from this screen's own panel
  // mount check. Kept as a local read too (viewedPlan/viewedPlanNotice
  // above) since DefensePlanViewer needs the actual plan/notice values, not
  // just the boolean.
  const { viewerOpen: showPlanViewer } = usePlanSurfaceOpen();
  // Widest left-docked panel currently mounted (the defense plan editor or
  // viewer, the only left-docked surface — see DockedPanel's `side` prop).
  const leftDockInset = useLeftDockInset();
  const [searchOverlayBottom, setSearchOverlayBottom] = useState(0);

  // Narrow store subscriptions: MapScreen doesn't watch shard data. The Map
  // component subscribes internally and updates Leaflet imperatively — this
  // screen only cares about UI overlays (loading/error banners, bottom
  // sheet visibility) and the action handles.
  const selectedStructure = useStructuresStore((s) => s.selectedStructure);
  const structuresLoading = useStructuresStore((s) => s.isLoading);
  const structuresError = useStructuresStore((s) => s.error);
  const selectStructure = useStructuresStore((s) => s.selectStructure);
  const getStructureById = useStructuresStore((s) => s.getStructureById);
  const clearStructuresError = useStructuresStore((s) => s.clearError);
  // "Show on map" from post detail: fetchPost is a cache-first read
  // (postsById, then the feed/user/structure lists, falling back to a
  // Firestore read) so this doesn't duplicate that lookup logic here.
  const fetchPost = usePostsStore((s) => s.fetchPost);
  // Wait for Firestore's active service-area list before touching shards.
  // The bootstrap fallback (src/store/serviceAreasStore.ts) seeds
  // `areas` with a hardcoded list that may diverge from what's currently
  // active in Firestore. Fetching on the bootstrap list caused pins to flash
  // briefly in areas the real server considers inactive, then vanish once
  // the subscription returned and `evictAreasNotIn` dropped them.
  const serviceAreasLoaded = useServiceAreasStore((s) => s.isLoaded);

  const loadedAreaIds = useStructuresStore((s) => s._loadedAreaIds);
  const loadedShardOrder = useStructuresStore((s) => s._loadedShardOrder);

  const user = useAuthStore((s) => s.user);
  const isModerator = useAuthStore((s) => s.isModerator);
  const areas = useServiceAreasStore((s) => s.areas);
  // Plan-map placement editor (Sept 2026 phase 2) — left-docked like the
  // defense plan editor, and mutually exclusive with it (see uiStore's
  // openPlanPanel/startOverlayPlacement).
  const placingOverlayId = useUIStore((s) => s.placingOverlayId);

  // Exit motion (Sept 2026 feel pass): a docked panel slides out over
  // durations.quick instead of vanishing. `instant` is true when another
  // surface on the same side is taking the slot in this same render — the
  // newcomer then replaces the old panel at once, as before, rather than two
  // panels sliding past each other. Surfaces the screen doesn't own
  // (property dock, composer, presentation) are covered by the store flags
  // read here where they exist.
  const composerDockOpen = useUIStore((s) => s.composerDockOpen);
  const propertySheetOpen = useUIStore((s) => s.activeSheet != null);
  // presentationActive covers fullscreen too, which hides the map anyway.
  const presentationActive = useUIStore((s) => s.presentationActive);
  const rightTaken = composerDockOpen || propertySheetOpen || presentationActive;
  const structureLeave = useLeavingPanel(selectedStructure != null, selectedStructure, {
    instant: dockedPostId != null || rightTaken,
  });
  const postLeave = useLeavingPanel(dockedPostId != null, dockedPostId, {
    instant: selectedStructure != null || rightTaken,
  });
  const planEditorOpen = planPanelOpen;
  const placementOpen = Boolean(placingOverlayId && isModerator);
  const planEditorLeave = useLeavingPanel(planEditorOpen, true, {
    instant: placementOpen || showPlanViewer,
  });
  const placementLeave = useLeavingPanel(placementOpen, true, {
    instant: planEditorOpen || showPlanViewer,
  });
  // Keeps the last plan/notice so the viewer doesn't blank while sliding out.
  const viewerLeave = useLeavingPanel(
    showPlanViewer,
    { plan: viewedPlan, notice: viewedPlanNotice },
    {
      instant: planEditorOpen || placementOpen,
    }
  );
  const layersLeave = useLeavingPanel(layersPanelOpen, true);
  const peopleLeave = useLeavingPanel(peoplePanelOpen, true);
  const programLeave = useLeavingPanel(programPanelOpen, true);

  // Everyone opens on Big Bear Lake — that's where the structures, claims,
  // and observations are. Web used to re-center on browser geolocation a
  // moment after first paint, which dropped out-of-area users into an empty
  // viewport that read as a broken app. Locate-me is still the escape hatch.
  const initialRegion: MapRegion = DEFAULT_REGION;

  const { hideTabBar, showTabBar } = useUIStore();
  // Reused to hide the (?) + avatar overlay whenever any bottom sheet is
  // open — same signal HideWhenSheetOpen uses for the map controls.
  const tabBarVisible = useUIStore((s) => s.tabBarVisible);
  const openSheet = useUIStore((s) => s.openSheet);
  const isUserMenuOpen = useUIStore((s) => s.activeSheet === 'userMenu');
  const hasSeenTour = useUIStore((s) => s.hasSeenTour);
  const hasDismissedSaveNudge = useUIStore((s) => s.hasDismissedSaveNudge);
  const hydrateSaveNudgeDismissal = useUIStore((s) => s.hydrateSaveNudgeDismissal);
  const hydrateTourSeen = useUIStore((s) => s.hydrateTourSeen);
  const hydrateMapStyle = useUIStore((s) => s.hydrateMapStyle);
  const hydrateMapPaths = useUIStore((s) => s.hydrateMapPaths);
  const mapSearchFocusRequested = useUIStore((s) => s.mapSearchFocusRequested);
  const clearMapSearchFocusRequest = useUIStore((s) => s.clearMapSearchFocusRequest);
  // Hydrate persisted UI flags once on mount so the avatar dot and the
  // map-paths overlay don't pop in then back out on a returning device.
  useEffect(() => {
    void hydrateSaveNudgeDismissal();
    void hydrateTourSeen();
    void hydrateMapStyle();
    void hydrateMapPaths();
  }, [hydrateSaveNudgeDismissal, hydrateTourSeen, hydrateMapStyle, hydrateMapPaths]);

  const mapRef = useRef<MapHandle>(null);
  const searchBarRef = useRef<AddressSearchBarHandle>(null);
  // Mirror `selectedStructure` so the sheet content doesn't flash empty
  // while the sheet animates closed. Cleared once `onChange(-1)` fires.
  const [sheetStructure, setSheetStructure] = useState<Structure | null>(null);
  useEffect(() => {
    if (selectedStructure) setSheetStructure(selectedStructure);
  }, [selectedStructure]);
  const snapPoints = useMemo(() => ['90%'], []);
  // Tab bar visibility is derived from whether the structure sheet is
  // showing. hideTabBar/showTabBar use a ref-counted implementation, so we
  // increment on open and decrement on close via effect cleanup.
  useEffect(() => {
    // Desktop shell: the docked panel overlays only the map's right edge, so
    // holding the overlay refcount would suppress SaveAccountToast for no
    // reason (the sidebar never hides).
    if (!selectedStructure || isDesktopShell) return;
    hideTabBar();
    return () => showTabBar();
  }, [selectedStructure, isDesktopShell, hideTabBar, showTabBar]);
  const [currentRegion, setCurrentRegion] = useState<MapRegion>(initialRegion);

  // Viewport bounds. Derived once here so the area-intersection and
  // shard-load effects both read the same numbers.
  const viewport = useMemo(() => {
    const south = currentRegion.latitude - currentRegion.latitudeDelta / 2;
    const north = currentRegion.latitude + currentRegion.latitudeDelta / 2;
    const west = currentRegion.longitude - currentRegion.longitudeDelta / 2;
    const east = currentRegion.longitude + currentRegion.longitudeDelta / 2;
    return { south, west, north, east };
  }, [currentRegion]);

  // Which active service areas overlap the visible map. Used to pick which
  // manifests to load and which areas to subscribe to for live claims.
  // Not surfaced to the user — the area concept is internal-only until
  // we focus on a specific community.
  const findIntersecting = useServiceAreasStore((s) => s.findIntersecting);
  const findContaining = useServiceAreasStore((s) => s.findContaining);

  // Whatever active area the map center sits over (if any). Drives the
  // optional `currentAreaId` scoping the search bar passes to
  // `searchStructures` — purely a search-quality choice, not user-visible.
  const currentArea = useMemo<ServiceArea | null>(
    () => findContaining(currentRegion.latitude, currentRegion.longitude),
    // areas dep forces recompute when Firestore hydrates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [findContaining, currentRegion, areas]
  );

  const serviceAreaIds = useMemo(() => {
    const hits = findIntersecting(viewport.south, viewport.west, viewport.north, viewport.east)
      .map((a) => a.id)
      .sort();
    if (hits.length > 0) return hits;
    // Fall back to already-loaded areas that are still active — don't reuse
    // ids that flipped to inactive (those shards are being evicted).
    const activeIds = new Set(areas.map((a) => a.id));
    const activeLoaded = loadedAreaIds.filter((id) => activeIds.has(id));
    return activeLoaded.length > 0 ? activeLoaded.sort() : [];
  }, [findIntersecting, viewport, loadedAreaIds, areas]);

  // When `areas` changes (e.g. Firestore toggled an area inactive), evict
  // any in-memory shards for areas no longer in the active list so the
  // map stops rendering their pins. Keyed on the stable id list so we
  // don't evict just because a name or timestamp changed.
  const activeAreaIdsKey = useMemo(
    () =>
      areas
        .map((a) => a.id)
        .sort()
        .join(','),
    [areas]
  );

  const serviceAreaIdsKey = serviceAreaIds.join(',');
  // Stable reference for the Map prop: only swaps when the ID set actually
  // changes, not on every viewport pan recompute. Keeps the memoized Map
  // from re-running its expensive `structures` flatten unnecessarily.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableActiveAreaIds = useMemo(() => serviceAreaIds, [serviceAreaIdsKey]);

  // Per-area loading state: true when we're loading AND no shard for the
  // current viewport's area is in memory yet. Drives the full-screen
  // "Loading structures" overlay on area switch, not just on first session
  // load — the user should always see a clear loading state when entering a
  // fresh area.
  const hasShardForCurrentArea = useMemo(() => {
    if (serviceAreaIds.length === 0) return true;
    const ids = new Set(serviceAreaIds);
    for (const key of loadedShardOrder) {
      const colon = key.indexOf(':');
      if (colon < 0) continue;
      if (ids.has(key.slice(0, colon))) return true;
    }
    return false;
  }, [loadedShardOrder, serviceAreaIds]);
  const isFirstLoad = !hasShardForCurrentArea && structuresLoading;
  // Rounded bounds key so pixel-level pan jitter doesn't trigger a reload.
  const viewportKey = `${viewport.south.toFixed(2)},${viewport.west.toFixed(2)},${viewport.north.toFixed(2)},${viewport.east.toFixed(2)}`;

  const { floatingMapPosts } = useMapSubscriptions({
    serviceAreaIds,
    serviceAreaIdsKey,
    activeAreaIdsKey,
    viewportKey,
    viewport,
    currentRegion,
    serviceAreasLoaded,
    areas,
    currentAreaId: currentArea?.id ?? null,
  });

  useMapFocus({ selectStructure, getStructureById, getPostById: fetchPost, mapRef });
  // Shareable panel deep links (panel-system.md Phase 4A) — plan/assign/
  // people/post query params, parsed once and independent of the
  // focusStructureId channel useMapFocus owns above.
  usePanelDeepLinks();
  // Insurer email links (`/?property=&model=`) prime the Property tab.
  usePropertyLinkIntake();

  // Takes the Post directly so it can be passed to the memo'd Map as-is —
  // an inline `(p) => handlePostPress(p.id)` wrapper broke the memo on every
  // pan and re-ran the marker/cluster work.
  const handlePostPress = useCallback(
    (post: Post) => {
      // Desktop shell: an observation drills into the right dock instead of
      // navigating away from the map (panel-system.md Phase 2). Dock
      // arbitration mirrors handleStructurePress/handleOpenSlideshow — the
      // structure panel and the presentation share this same right-hand
      // surface, so opening the post clears both.
      if (isDesktopShell) {
        selectStructure(null);
        setViewedPlanId(null);
        requestSlideshowClose();
        closeSheet();
        closeComposerDock();
        openDockedPost(post.id);
        return;
      }
      router.push(`/post/${post.id}`);
    },
    [
      isDesktopShell,
      selectStructure,
      setViewedPlanId,
      requestSlideshowClose,
      closeSheet,
      closeComposerDock,
      openDockedPost,
    ]
  );

  const handleRegionChange = useCallback((region: MapRegion) => {
    setCurrentRegion(region);
  }, []);

  const handleStructurePress = useCallback(
    (structure: Structure) => {
      // Opening the sheet is a derived effect of `selectedStructure` — see
      // the BottomSheet's `index` prop below. Nothing imperative here.
      // Dock arbitration: a selected structure, the plan editor and the plan
      // viewer all share the same right-docked surface, so picking a
      // structure closes the editor and hides the viewer (any plan drawn on
      // the map stays drawn — only the panel/card goes).
      closePlanPanel();
      setViewedPlanId(null);
      closeDockedPost();
      // addProperty/propertyPicker only dock (PropertyDockPanel.web.tsx) on
      // the desktop shell — leave other sheets alone on mobile.
      if (isDesktopShell) closeSheet();
      closeComposerDock();
      selectStructure(structure);
      // Pan only when the house sits outside the part of the map the dock
      // will leave clear (the dock is still sliding in, so pass its width
      // rather than reading the store). A house already in view keeps the
      // camera exactly where it is.
      mapRef.current?.ensureInClearView?.(
        structure.centroid.latitude,
        structure.centroid.longitude,
        { insets: structureFocusInsets(isDesktopShell) }
      );
    },
    [
      selectStructure,
      closePlanPanel,
      setViewedPlanId,
      closeDockedPost,
      closeSheet,
      closeComposerDock,
      isDesktopShell,
    ]
  );

  const handleSelectFromSearch = useCallback(
    (structure: Structure) => {
      closePlanPanel();
      setViewedPlanId(null);
      closeDockedPost();
      if (isDesktopShell) closeSheet();
      closeComposerDock();
      selectStructure(structure);
      // Explicit street-level zoom: the web default (15) sits below
      // STRUCTURE_RENDER_MIN_ZOOM_WEB, so the searched structure's marker
      // wouldn't render. 17 matches the deep-link focus below and shows
      // address labels.
      const handle = mapRef.current;
      const { latitude, longitude } = structure.centroid;
      if (handle?.focusInClearView) {
        handle.focusInClearView(latitude, longitude, 17, {
          insets: structureFocusInsets(isDesktopShell),
        });
      } else {
        handle?.focusOnLocation(latitude, longitude, 17);
      }
    },
    [
      selectStructure,
      closePlanPanel,
      setViewedPlanId,
      closeDockedPost,
      closeSheet,
      closeComposerDock,
      isDesktopShell,
    ]
  );

  // MapPathsOverlay's Property card: focus the search bar in place rather
  // than navigating anywhere, so the overlay fading out reveals the input
  // the user is about to type into.
  const handleEvaluateFromOverlay = useCallback(() => {
    searchBarRef.current?.focus();
  }, []);

  // The Property tab's no-claimed-structure fallback (useOpenProperty):
  // the press lands here through uiStore.requestMapSearchFocus because the
  // tab can be pressed from any screen while the search bar ref lives on
  // this one. Clear-before-focus so a failed focus can't wedge the one-shot.
  // AddressSearchBar consumes the request itself (focusRequested below) and
  // clears it once its input has focus, which survives the bar mounting
  // late or remounting on arrival from another route (an insurer page).
  // This only drops a request that never landed, so it can't fire on some
  // later visit to the map.
  useEffect(() => {
    if (!mapSearchFocusRequested) return;
    const timer = setTimeout(clearMapSearchFocusRequest, 5000);
    return () => clearTimeout(timer);
  }, [mapSearchFocusRequested, clearMapSearchFocusRequest]);

  const structureSheetRef = useRef<BottomSheet>(null);
  const handleCloseSheet = useCallback(() => {
    // Imperatively close the sheet in addition to flipping selectedStructure.
    // On iOS (gorhom v5 + Reanimated 4 under Fabric) the declarative
    // `index={-1}` change doesn't always kick the close animation, so the
    // sheet would stay visible even though the selection cleared.
    structureSheetRef.current?.close();
    selectStructure(null);
  }, [selectStructure]);

  // Sync sheet content with selection. Only clear sheetStructure after the
  // sheet finishes animating closed so the content doesn't blink empty.
  const handleSheetChange = useCallback(
    (index: number) => {
      if (index === -1) {
        if (selectedStructure) selectStructure(null);
        setSheetStructure(null);
      }
    },
    [selectedStructure, selectStructure]
  );

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Head>
        <title>Map - Redi</title>
      </Head>
      {/* Map */}
      {/* Z_INDEX_AUTO so the map's hover card can rise above the search
          overlay below; without it this wrapper caps everything inside the
          map at its own z-index. While isFirstLoad dims the map its opacity
          makes a stacking context anyway, but nothing is hoverable yet. */}
      <View style={[isFirstLoad ? styles.mapDimmed : styles.mapFull, Z_INDEX_AUTO]}>
        <Map
          ref={mapRef}
          initialRegion={initialRegion}
          activeAreaIds={stableActiveAreaIds}
          showUserLocation
          posts={floatingMapPosts}
          onRegionChange={handleRegionChange}
          onStructurePress={handleStructurePress}
          onPostPress={handlePostPress}
        />
      </View>

      {/* Search bar overlay. Desktop shell: collapses to a corner icon at
          the map's top-left (searchOverlayDesktop), same spot a docked
          panel's inset shifts right — see AddressSearchBar's `collapsible`
          prop. Phone/tablet: unchanged, the full-width field centered at
          the top. */}
      <View
        // Measured so the phone plan viewer card can sit just under it.
        onLayout={(e) =>
          setSearchOverlayBottom(e.nativeEvent.layout.y + e.nativeEvent.layout.height)
        }
        style={[
          styles.searchOverlay,
          {
            // Desktop has no safe-area inset, so this was landing the bar
            // 8px (SPACING.sm) from the top instead of the 16px every other
            // floating map control sits off its edge (Sept 29 2026, M1).
            // Phone's own insets.top already clears the notch/status bar.
            paddingTop: insets.top + (isDesktopShell ? SPACING.md : SPACING.sm),
          },
          isDesktopShell && styles.searchOverlayDesktop,
          // Animated so the bar doesn't jump-cut once a panel finishes its
          // own slide-in.
          isDesktopShell && TRANSITION_DOCK_INSET,
          // Shifts right to clear the defense plan editor/viewer, the one
          // panel docked on the left — same inset the docked panel itself
          // reads (useLeftDockInset in uiStore). The collapsed/expanded bar
          // no longer reaches the right edge, so there's nothing to clear
          // on that side any more.
          isDesktopShell && leftDockInset > 0 && { left: leftDockInset },
        ]}>
        <AddressSearchBar
          ref={searchBarRef}
          onSelectStructure={handleSelectFromSearch}
          onAddProperty={(q) => openSheet('addProperty', { addPropertyInitialQuery: q })}
          currentAreaId={currentArea?.id ?? null}
          currentAreaName={currentArea?.name ?? null}
          collapsible={isDesktopShell}
          focusRequested={mapSearchFocusRequested}
          onFocusRequestHandled={clearMapSearchFocusRequest}
        />
        {structuresError && (
          // Surfaced when the initial GeoJSON fetch or the claims
          // subscription errors. Auto-clears on the next successful load
          // because withLoading resets `error` at the start of each call.
          <Pressable
            onPress={clearStructuresError}
            style={[styles.banner, { backgroundColor: colors.dangerSoft }]}
            accessibilityRole="button"
            accessibilityLabel="Dismiss map error">
            <Text style={[styles.bannerText, { color: colors.danger }]} numberOfLines={2}>
              {structuresError}
            </Text>
          </Pressable>
        )}
      </View>

      {/* (?) help icon + avatar/Sign In chip. On wider viewports they sit
          in the top-right corner alongside the centered search bar. On
          mobile the search bar spans full-width and would collide, so the
          group moves to the bottom-right and stacks vertically above the
          map controls. Hidden when the structure detail sheet is open (its
          inline BottomSheet is a sibling on web, so without this gate the
          overlay punches through on top) AND when any other sheet hides the
          tab bar (mirrors HideWhenSheetOpen for MapControls). */}
      <MapSignInOverlay
        user={user}
        isDesktopShell={isDesktopShell}
        tabBarVisible={tabBarVisible}
        selectedStructure={selectedStructure}
        isMobile={isMobile}
        insets={insets}
        hasSeenTour={hasSeenTour}
        hasDismissedSaveNudge={hasDismissedSaveNudge}
        isUserMenuOpen={isUserMenuOpen}
        openSheet={openSheet}
      />

      {/* Call to action over the map: three translucent cards (evaluate a
          property / post an observation / post news). No user gate —
          signed-out visitors are the point. Shows on first visit and
          whenever the Guidance tab re-summons it; shared visibility
          selector lives in uiStore (useMapPathsOverlayActive). */}
      {/* Field mode (phone website) drops the Guidance overlay entirely. */}
      {!fieldMode && (
        <MapPathsOverlay onEvaluate={handleEvaluateFromOverlay} mapLoading={isFirstLoad} />
      )}

      {/* One-time coachmark pointing at the floating + button. Self-gated:
          renders nothing once dismissed, when the user has already posted,
          or when a structure detail sheet is open — and, now, while the
          map-paths overlay is still up so the two prompts never stack. */}
      <CreatePostCoachmark />

      {/* Inline nudge for anonymous Fire Scouts to save their account
          after they post. Self-gated on uiStore.showSaveAccountToast. */}
      <SaveAccountToast />

      {/* Map key. Explains the two channels the map encodes — shape for what a
          marker is, color for how far along a property is. Hidden while a
          detail sheet is up (it would sit under the sheet on mobile and beside
          the docked panel on desktop, competing for the same glance) and while
          the map is still loading. */}
      {!isFirstLoad && selectedStructure == null && <MapLegend />}

      {/* Loading overlay */}
      <MapLoadingOverlay isFirstLoad={isFirstLoad} />

      {/* Structure detail sheet — open state derived from `selectedStructure`.
          On web, a closed (index=-1) inline BottomSheet doesn't translate fully
          off-screen and leaves an opaque panel over the bottom of the map at
          desktop heights, so mount it only while open there. Native keeps the
          always-mounted sheet (and its close animation). */}
      {isDesktopShell && structureLeave.mounted && structureLeave.lastContent != null && (
        <DockedPanel scroll={false} shown={structureLeave.shown} onExited={structureLeave.onExited}>
          <StructureDetailPanel structure={structureLeave.lastContent} onClose={handleCloseSheet} />
        </DockedPanel>
      )}

      {/* Observation panel — right-docked, same surface as the structure
          panel above and the presentation (see handlePostPress and
          Map.web.tsx's handleOpenSlideshow): opening one clears the other
          two. Desktop shell only; below it a marker click still navigates to
          /post/[id] (panel-system.md Phase 2). scroll={false}: the header
          pins inside PostDetailPanel, which owns its own inner ScrollView
          (panel-system.md review, note 4). */}
      {isDesktopShell && postLeave.mounted && postLeave.lastContent != null && (
        <DockedPanel
          testID="post-panel"
          width={DETAIL_PANEL_WIDTH}
          scroll={false}
          shown={postLeave.shown}
          onExited={postLeave.onExited}
          ariaLabelledBy={POST_PANEL_HEADING_ID}>
          <PostDetailPanel postId={postLeave.lastContent} onClose={closeDockedPostAndPop} />
        </DockedPanel>
      )}

      {/* Add-property / property-picker — right-docked on the desktop map
          route only (app/_layout.tsx suppresses the two global AdaptiveSheet
          hosts there); PropertyDockPanel itself gates on `activeSheet` and
          arbitrates with the structure/post/presentation surfaces above
          (panel-system.md Phase 2). Elsewhere (profile, the structure page,
          phone) those two AdaptiveSheet hosts stay. */}
      {isDesktopShell && <PropertyDockPanel />}

      {/* Docked observation composer (panel-system.md Phase 3A) — right-hand
          dock surface, same arbitration group as the three above. Self-gates
          on constants/composer.ts's DESKTOP_COMPOSER_DOCK_ENABLED and
          uiStore.composerDockOpen. */}
      {isDesktopShell && <ComposerDockPanel />}

      {/* Defense plan editor — LEFT-docked (Sept 2026 client ask: "come out
          from the left side and cover up" the sidebar), unlike the
          structure sheet and the presentation panel which stay on the
          right. Dock arbitration (above and in Map.web.tsx's open-request
          consumer) still keeps only one of the three on screen. The sidebar
          no longer hides for this — it collapses to a 64px rail on this
          screen instead (AppShell, SidebarNav's `variant` prop) and stays
          up while planning; this panel's `left: 0` resolves inside
          AppShell's content pane, which already starts to the right of
          whatever width the rail/sidebar currently takes, so the two never
          overlap. */}
      {isDesktopShell && planEditorLeave.mounted && (
        <DockedPanel
          width={PLAN_PANEL_WIDTH}
          testID="defense-plan-panel"
          side="left"
          scroll={false}
          shown={planEditorLeave.shown}
          onExited={planEditorLeave.onExited}>
          <DefensePlanPanel />
        </DockedPanel>
      )}

      {/* Plan-map placement editor — same left dock as the defense plan
          editor above, and mutually exclusive with it (uiStore closes one
          whenever the other opens). Moderator-only: placingOverlayId can
          only be set from the Layers sheet's moderator-only rows, but this
          guards the panel itself too rather than trusting that alone. */}
      {isDesktopShell && placementLeave.mounted && (
        <DockedPanel
          width={PLAN_PANEL_WIDTH}
          testID="plan-map-placement-panel"
          side="left"
          shown={placementLeave.shown}
          onExited={placementLeave.onExited}>
          <ErrorBoundary
            fallback={() => (
              <Text style={[styles.chunkErrorText, { color: colors.textSecondary }]}>
                Couldn&apos;t load the editor. Refresh the page.
              </Text>
            )}>
            <React.Suspense fallback={null}>
              <LazyPlanMapPlacementPanel />
            </React.Suspense>
          </ErrorBoundary>
        </DockedPanel>
      )}

      {/* Read-only plan viewer — a share link (app/plan/[id].tsx) or a
          "plans in this area" card points uiStore.viewedPlanId at a plan;
          same LEFT-docked surface as the editor, so it only shows while
          the editor isn't (showPlanViewer above), and picking a structure or
          opening the editor both hide it (see handleStructurePress and
          Map.web.tsx's slideshow/plan-panel-open effects). Below the
          desktop shell there's no left rail to dock to, so the same plan
          gets a floating card over the map instead — web only, since native
          never renders a plan (viewedPlanId never gets set there). */}
      {isDesktopShell && viewerLeave.mounted && (
        <DockedPanel
          width={PLAN_PANEL_WIDTH}
          testID="plan-viewer-panel"
          side="left"
          shown={viewerLeave.shown}
          onExited={viewerLeave.onExited}>
          <DefensePlanViewer
            plan={viewerLeave.lastContent?.plan ?? null}
            notice={viewerLeave.lastContent?.notice ?? null}
          />
        </DockedPanel>
      )}
      {!isDesktopShell && Platform.OS === 'web' && showPlanViewer && (
        <DefensePlanViewerCard
          plan={viewedPlan}
          notice={viewedPlanNotice}
          top={searchOverlayBottom + SPACING.xs}
        />
      )}

      {/* Map layers — LEFT-docked like the three plan surfaces above, and
          mounted after all of them on purpose: they share one z-index
          (Z_INDEX.dockedPanel), so mount order alone decides which one
          paints on top, and Layers opened over an already-open editor or
          viewer should cover it rather than disappear behind it (uiStore
          keeps the editor's own state untouched — see toggleLayersPanel). */}
      {isDesktopShell && layersLeave.mounted && (
        <MapLayersPanel shown={layersLeave.shown} onExited={layersLeave.onExited} />
      )}

      {/* People (uploaded leader lists): the same left dock and z-index,
          mounted last so it paints over Layers or the plan editor the way
          Layers does over the editor. Opening one closes the other (uiStore
          togglePeoplePanel / toggleLayersPanel). */}
      {isDesktopShell && peopleLeave.mounted && (
        <PeoplePanel shown={peopleLeave.shown} onExited={peopleLeave.onExited} />
      )}

      {/* Program (moderators): same left dock and z-index, mounted after
          People for the same reason. The three browse panels close each
          other (uiStore toggleProgramPanel). */}
      {isDesktopShell && programLeave.mounted && (
        <DockedPanel
          side="left"
          width={PLAN_PANEL_WIDTH}
          scroll={false}
          testID="program-panel"
          ariaLabelledBy={PROGRAM_PANEL_HEADING_ID}
          shown={programLeave.shown}
          onExited={programLeave.onExited}>
          <ErrorBoundary
            fallback={() => (
              <Text style={[styles.chunkErrorText, { color: colors.textSecondary }]}>
                Couldn&apos;t load the program. Refresh the page.
              </Text>
            )}>
            <React.Suspense fallback={null}>
              <LazyProgramPanelBody shown={programLeave.shown} />
            </React.Suspense>
          </ErrorBoundary>
        </DockedPanel>
      )}

      {!isDesktopShell && (Platform.OS !== 'web' || selectedStructure != null) && (
        <BottomSheet
          ref={structureSheetRef}
          index={selectedStructure ? 0 : -1}
          snapPoints={snapPoints}
          // gorhom v5's enableDynamicSizing default adds a content-height
          // snap point alongside the 90% — intentional: the sheet opens at
          // content height (index 0) and 90% is the expanded stop.
          accessible={false}
          enablePanDownToClose
          // Native keeps this sheet mounted for the life of the screen, so
          // animating on mount would slide it up at app launch. Web mounts it
          // only while something is selected (see the gate above), so there
          // mount IS the open, and suppressing the animation made the sheet
          // appear in place instead of rising. The flag predates that web
          // gate: it landed in b178d6d (May 27) for the always-mounted sheet,
          // and bb8e259 (Jun 10) changed web to mount-on-open without
          // revisiting it.
          animateOnMount={Platform.OS === 'web'}
          onChange={handleSheetChange}
          backgroundStyle={{ backgroundColor: colors.surfaceElevated }}
          handleIndicatorStyle={{ backgroundColor: colors.border }}
          // Desktop: cap to 720 + center, same as every other sheet. The
          // only sheet that was missing useDesktopSheetStyle.
          style={sheetStyle}>
          <BottomSheetView>
            {sheetStructure && (
              <StructureDetailSheet structure={sheetStructure} onClose={handleCloseSheet} />
            )}
          </BottomSheetView>
        </BottomSheet>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  searchOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    paddingHorizontal: SPACING.md,
    maxWidth: 720,
    marginHorizontal: 'auto',
    zIndex: 100,
  },
  // Desktop shell: the bar itself (AddressSearchBar) now sizes to its own
  // 44px/360px width instead of stretching to fill a centered box, so this
  // drops the phone/tablet centering in favor of a plain top-left anchor.
  // `left` is 0 with no panel docked and the leftDockInset once one is; an
  // audit pins that value to PLAN_PANEL_WIDTH, so the gap is padding instead.
  searchOverlayDesktop: {
    left: 0,
    right: 'auto',
    maxWidth: 420,
    marginHorizontal: 0,
    // The gap lives inside the box, so the button keeps the same breathing
    // room from the map's edge and from a docked panel's edge (whose inset
    // replaces `left` exactly).
    paddingLeft: SPACING.md,
    paddingRight: 0,
  },
  mapFull: {
    flex: 1,
  },
  mapDimmed: {
    flex: 1,
    opacity: 0.4,
  },
  banner: {
    marginTop: SPACING.sm,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.sm,
    borderRadius: 8,
  },
  bannerText: {
    ...TYPOGRAPHY.button,
    textAlign: 'center',
  },
  chunkErrorText: {
    ...TYPOGRAPHY.bodySmall,
    padding: SPACING.md,
  },
});
